import type { Feature } from 'geojson';
import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import type { TripDetail } from '../types';

const ROUTE_SOURCE = 'ilm-trip-route';
const STOP_SOURCE = 'ilm-trip-stops';

type FeatureCollection = {
  type: 'FeatureCollection';
  features: Feature[];
};

const empty = (): FeatureCollection => ({ type: 'FeatureCollection', features: [] });

/**
 * Draws the selected vehicle's route and its stops as real map layers, so they
 * sit underneath the vehicle canvas and stay correct while panning or tilting.
 */
export class TripLayer {
  private trip: TripDetail | null = null;
  private color = '#5b8cff';

  constructor(private readonly map: MapLibreMap) {
    // Sources can only be added once the style exists; `style.load` also fires
    // again after a theme swap, which drops every custom source and layer.
    if (map.isStyleLoaded()) this.install();
    map.on('style.load', () => {
      this.install();
      this.render();
    });
  }

  private install(): void {
    const map = this.map;
    if (!map.getSource(ROUTE_SOURCE)) {
      map.addSource(ROUTE_SOURCE, { type: 'geojson', data: empty() });
    }
    if (!map.getSource(STOP_SOURCE)) {
      map.addSource(STOP_SOURCE, { type: 'geojson', data: empty() });
    }

    if (!map.getLayer('ilm-route-casing')) {
      map.addLayer({
        id: 'ilm-route-casing',
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#000000',
          'line-opacity': 0.35,
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 10, 17, 15],
          'line-blur': 1.5,
        },
      });
    }
    if (!map.getLayer('ilm-route')) {
      map.addLayer({
        id: 'ilm-route',
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2.5, 14, 5, 17, 8],
        },
      });
    }
    if (!map.getLayer('ilm-stops')) {
      map.addLayer({
        id: 'ilm-stops',
        type: 'circle',
        source: STOP_SOURCE,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 13, 4, 16, 6],
          'circle-color': '#ffffff',
          'circle-stroke-color': ['get', 'color'],
          'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 10, 1, 14, 2],
        },
      });
    }
  }

  show(trip: TripDetail, color: string): void {
    this.trip = trip;
    this.color = color;
    this.render();
  }

  clear(): void {
    this.trip = null;
    this.render();
  }

  /** Bounds of the current route, for "fit to trip". */
  bounds(): [[number, number], [number, number]] | null {
    const shape = this.trip?.shape;
    if (!shape || shape.length < 2) return null;
    let west = 180;
    let south = 90;
    let east = -180;
    let north = -90;
    for (const [lon, lat] of shape) {
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
    return [
      [west, south],
      [east, north],
    ];
  }

  private render(): void {
    const routeSource = this.map.getSource(ROUTE_SOURCE) as GeoJSONSource | undefined;
    const stopSource = this.map.getSource(STOP_SOURCE) as GeoJSONSource | undefined;
    if (!routeSource || !stopSource) return;

    if (!this.trip || this.trip.shape.length < 2) {
      routeSource.setData(empty());
      stopSource.setData(empty());
      return;
    }

    routeSource.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: { color: this.color },
          geometry: { type: 'LineString', coordinates: this.trip.shape },
        },
      ],
    });

    stopSource.setData({
      type: 'FeatureCollection',
      features: this.trip.stopovers
        .filter((stop) => stop.lat !== null && stop.lon !== null)
        .map((stop) => ({
          type: 'Feature' as const,
          properties: { color: this.color, name: stop.name },
          geometry: { type: 'Point' as const, coordinates: [stop.lon as number, stop.lat as number] },
        })),
    });
  }
}
